// MessagesPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Send,
  MessageSquare,
  Sparkles,
  ShieldCheck,
  Users,
} from "lucide-react";
import "./MessagesPage.css";

function fmtAt(iso) {
  try {
    const d = new Date(iso);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
    }
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

const ME_ID = "me";

const INITIAL_STORE = {
  counterparties: {
    u1: {
      user_id: "u1",
      name: "Ajay",
      avatar_url: "",
      title: "Founder",
      company: "Anyon AI",
      is_vip: true,
    },
    u2: {
      user_id: "u2",
      name: "J",
      avatar_url: "",
      title: "Operator",
      company: "Greentide",
      is_vip: false,
    },
    u3: {
      user_id: "u3",
      name: "H",
      avatar_url: "",
      title: "Investor",
      company: "North Capital",
      is_vip: true,
    },
  },
  messages: {
    u1: [
      {
        id: "m-1",
        from_id: "u1",
        to_id: ME_ID,
        body: "Quick question — are you open to an intro on EU climate inference infra?",
        created_at: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
        read: false,
      },
      {
        id: "m-2",
        from_id: ME_ID,
        to_id: "u1",
        body: "Yes. If you share 2–3 sentences on the ask + stage, I'll route it.",
        created_at: new Date(Date.now() - 1000 * 60 * 60 * 23).toISOString(),
        read: true,
      },
    ],
    u2: [
      {
        id: "m-3",
        from_id: "u2",
        to_id: ME_ID,
        body: "Loved the table. Want to swap notes on procurement + pilots?",
        created_at: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
        read: false,
      },
    ],
    u3: [
      {
        id: "m-4",
        from_id: ME_ID,
        to_id: "u3",
        body: "Thanks again — sending the memo we mentioned after the salon.",
        created_at: new Date(Date.now() - 1000 * 60 * 10).toISOString(),
        read: true,
      },
    ],
  },
};

function buildThreads(store) {
  const out = [];
  for (const userId of Object.keys(store.counterparties || {})) {
    const cp = store.counterparties[userId];
    const msgs = store.messages[userId] || [];
    const last = msgs[msgs.length - 1] || null;
    const unread = msgs.filter(
      (m) => m.to_id === ME_ID && m.from_id === userId && !m.read,
    ).length;
    out.push({
      user_id: userId,
      avatar_url: cp.avatar_url,
      name: cp.name,
      title: cp.title,
      company: cp.company,
      is_vip: !!cp.is_vip,
      last_body: last?.body || "",
      last_at: last?.created_at || new Date().toISOString(),
      last_from_me: last ? last.from_id === ME_ID : false,
      unread,
    });
  }
  out.sort(
    (a, b) => new Date(b.last_at).getTime() - new Date(a.last_at).getTime(),
  );
  return out;
}

export default function MessagesPage() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [store, setStore] = useState(INITIAL_STORE);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    setThreads(buildThreads(store));
    setLoading(false);
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setThreads(buildThreads(store));
  }, [store]);

  return (
    <div data-testid="messages-page" className="messagesPage">
      <section
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
        className="messagesHeroSection"
      >
        <div className="container-x messagesHeroInner">
          <div className="messagesHeroTop">
            <div>
              <p className="overline messagesHeroOverline">
                <MessageSquare size={12} className="messagesHeroOverlineIcon" />{" "}
                Messages
              </p>
              <h1 className="messagesHeroHeading">
                Quiet conversations,
                <br />
                <span className="messagesHeroHeadingAccent">
                  sent on purpose.
                </span>
              </h1>
            </div>
            <Link to="/network" className="messagesFindLink">
              <Users size={12} /> Find someone to message
            </Link>
          </div>

          <div className="messagesLayout">
            <aside
              data-testid="thread-list"
              className="glass threadListPanel"
            >
              {loading && (
                <p className="threadListLoading">Loading…</p>
              )}
              {!loading && threads.length === 0 && (
                <div className="threadListEmpty">
                  <Sparkles size={18} className="threadListEmptyIcon" />
                  <p className="threadListEmptyTitle">No messages yet.</p>
                  <p className="threadListEmptyBody">
                    Send the first one — your message lands as{" "}
                    <em>Anonymous · Role at Company</em> until you both opt in.
                  </p>
                  <Link to="/network" className="threadListEmptyLink">
                    Browse the network →
                  </Link>
                </div>
              )}
              <ul className="threadList">
                {threads.map((t) => (
                  <li key={t.user_id}>
                    <button
                      onClick={() => nav(`/messages/${t.user_id}`)}
                      data-testid={`thread-item-${t.user_id}`}
                      className={`threadItem ${
                        userId === t.user_id ? "threadItemActive" : ""
                      }`}
                    >
                      <span className="threadItemAvatar">
                        {t.avatar_url ? (
                          <img
                            src={t.avatar_url}
                            alt=""
                            className="threadItemAvatarImg"
                          />
                        ) : (
                          (t.name || "?").slice(0, 1).toUpperCase()
                        )}
                      </span>
                      <span className="threadItemBody">
                        <span className="threadItemNameRow">
                          <span className="threadItemName">
                            {t.title || "Member"}{" "}
                            <span className="threadItemCompany">
                              at {t.company || "—"}
                            </span>
                          </span>
                          {t.is_vip && (
                            <ShieldCheck
                              size={11}
                              className="threadItemVip"
                            />
                          )}
                        </span>
                        <span className="threadItemPreview">
                          {t.last_from_me ? "You: " : ""}
                          {t.last_body}
                        </span>
                      </span>
                      <span className="threadItemMeta">
                        <span className="threadItemTime">
                          {fmtAt(t.last_at)}
                        </span>
                        {t.unread > 0 && (
                          <span
                            data-testid={`thread-unread-${t.user_id}`}
                            className="threadItemUnread"
                          >
                            {t.unread}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            <main className="glass threadMain">
              {userId ? (
                <ThreadView
                  userId={userId}
                  store={store}
                  setStore={setStore}
                  onSent={reload}
                />
              ) : (
                <EmptyThreadState hasThreads={threads.length > 0} />
              )}
            </main>
          </div>
        </div>
      </section>
    </div>
  );
}

function EmptyThreadState({ hasThreads }) {
  return (
    <div className="emptyThreadState">
      <MessageSquare size={32} className="emptyThreadStateIcon" />
      <p className="emptyThreadStateTitle">
        {hasThreads ? "Pick a conversation" : "No conversations yet"}
      </p>
      <p className="emptyThreadStateBody">
        {hasThreads
          ? "Choose a thread on the left to read it. Inbound messages mark themselves read when you open them."
          : "Send your first DM from the network directory or after meeting someone at a table."}
      </p>
    </div>
  );
}

function ThreadView({ userId, store, setStore, onSent }) {
  const [data, setData] = useState({ counterparty: null, messages: [] });
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const load = () => {
    const cp = store.counterparties?.[userId] || null;
    const msgs = store.messages?.[userId] || [];
    setData({ counterparty: cp, messages: msgs });
  };

  useEffect(() => {
    load();
    setStore((prev) => {
      const msgs = prev.messages?.[userId] || [];
      const updated = msgs.map((m) =>
        m.to_id === ME_ID && m.from_id === userId ? { ...m, read: true } : m,
      );
      return { ...prev, messages: { ...prev.messages, [userId]: updated } };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, userId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [data.messages.length]);

  const send = async (e) => {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    try {
      const msg = {
        id: `m-${Math.random().toString(16).slice(2)}`,
        from_id: ME_ID,
        to_id: userId,
        body: text,
        created_at: new Date().toISOString(),
        read: true,
      };
      setStore((prev) => {
        const msgs = prev.messages?.[userId] || [];
        return {
          ...prev,
          messages: { ...prev.messages, [userId]: [...msgs, msg] },
        };
      });
      setBody("");
      if (onSent) onSent();
    } catch {
      toast.error("Could not send.");
    } finally {
      setBusy(false);
    }
  };

  if (!data.counterparty) {
    return (
      <div className="threadLoading" data-testid="thread-loading">
        Loading conversation…
      </div>
    );
  }

  const cp = data.counterparty;
  return (
    <div
      className="threadViewRoot"
      data-testid={`thread-view-${userId}`}
    >
      <div className="threadViewHeader">
        <Link to="/messages" className="threadViewBackLink">
          <ArrowLeft size={16} />
        </Link>
        <span className="threadViewCpAvatar">
          {cp.avatar_url ? (
            <img
              src={cp.avatar_url}
              alt=""
              className="threadViewCpAvatarImg"
            />
          ) : (
            (cp.name || "?").slice(0, 1).toUpperCase()
          )}
        </span>
        <div className="threadViewCpInfo">
          <p className="threadViewCpName">
            {cp.title || "Member"}{" "}
            <span className="threadViewCpCompany">
              at {cp.company || "—"}
            </span>
          </p>
          <p className="threadViewCpAnon">
            Names hidden until you both opt in
          </p>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="threadMessages"
        data-testid="thread-messages"
      >
        {data.messages.length === 0 && (
          <p className="threadMessagesEmpty">
            Start the conversation. Be specific — what trade are you proposing?
          </p>
        )}
        {data.messages.map((m) => {
          const mine = m.to_id === userId;
          return (
            <div
              key={m.id}
              className={`threadBubbleRow ${mine ? "threadBubbleRowMine" : "threadBubbleRowTheirs"}`}
            >
              <div
                className={`threadBubble ${mine ? "threadBubbleMine" : "threadBubbleTheirs"}`}
              >
                <p>{m.body}</p>
                <p
                  className={`threadBubbleTime ${mine ? "threadBubbleTimeMine" : "threadBubbleTimeTheirs"}`}
                >
                  {fmtAt(m.created_at)}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <form
        onSubmit={send}
        className="threadComposer"
        data-testid="thread-composer"
      >
        <input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Send a message…"
          className="threadComposerInput"
          maxLength={2000}
          autoComplete="off"
          data-testid="thread-input"
        />
        <button
          type="submit"
          disabled={busy || !body.trim()}
          data-testid="thread-send"
          className="threadComposerSend"
        >
          <Send size={15} />
        </button>
      </form>
    </div>
  );
}